import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useMode } from '../context/ModeContext';
import {
  MagnifyingGlass, TrendUp, Lightning, Handshake, CalendarCheck,
  ChatText, Megaphone, UserPlus, EnvelopeSimple, Phone, ArrowRight,
  CircleNotch, CheckCircle, Rocket, Sparkle, CaretRight, X,
  Robot, Play, TestTube, Warning, Users, PencilSimple, Plugs
} from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════════
   CATEGORY MAPPING — Frontend-only presentation layer
   Maps backend template IDs/categories to outcome buckets.
   Backend schema is UNTOUCHED.
   ═══════════════════════════════════════════════════════ */

const OUTCOME_CATEGORIES = [
  {
    id: 'get-more-leads',
    label: 'Get More Leads',
    description: 'Attract and capture new customers automatically',
    icon: UserPlus,
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    borderColor: 'rgba(16, 185, 129, 0.25)',
  },
  {
    id: 'respond-win-faster',
    label: 'Respond & Win Faster',
    description: 'Never miss an enquiry — reply in seconds, not hours',
    icon: Lightning,
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    borderColor: 'rgba(245, 158, 11, 0.25)',
  },
  {
    id: 'follow-up-close',
    label: 'Follow Up & Close More Deals',
    description: 'Automatic follow-ups that turn leads into paying customers',
    icon: Handshake,
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.08)',
    borderColor: 'rgba(139, 92, 246, 0.25)',
  },
  {
    id: 'book-manage-jobs',
    label: 'Book & Manage Jobs',
    description: 'Schedule, invoice, and manage jobs without the admin',
    icon: CalendarCheck,
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.08)',
    borderColor: 'rgba(59, 130, 246, 0.25)',
  },
];

/* Map each backend template to an outcome category + outcome-first display name */
const TEMPLATE_OUTCOME_MAP = {
  // ── Get More Leads ──
  'template_a6d25df47c04': { cat: 'get-more-leads', name: 'Generate content ideas that attract leads' },
  'template_43a75b98e329': { cat: 'get-more-leads', name: 'Post to social media on autopilot' },
  'template_db4587c267e0': { cat: 'get-more-leads', name: 'Run email campaigns that bring in enquiries' },
  'template_3ea274ac7235': { cat: 'get-more-leads', name: 'Get found on Google with SEO-optimised content' },
  'template_52e1111e9b8f': { cat: 'get-more-leads', name: 'Deliver lead magnets and capture contacts' },
  'social_calendar_v1':     { cat: 'get-more-leads', name: 'Plan a month of social content in minutes' },
  'email_marketing_v1':     { cat: 'get-more-leads', name: 'Build email campaigns that convert' },
  'landing_page_v1':        { cat: 'get-more-leads', name: 'Write landing page copy that converts visitors' },
  'blog_writer_v1':         { cat: 'get-more-leads', name: 'Write blog posts that rank and attract leads' },
  'content-calendar-strategist': { cat: 'get-more-leads', name: 'Plan your content calendar for maximum reach' },
  'seo-brief-generator':    { cat: 'get-more-leads', name: 'Create SEO briefs to rank higher' },
  'ad-copy-variant-tester': { cat: 'get-more-leads', name: 'Test ad copy variations to find winners' },
  'website-copy-optimizer':  { cat: 'get-more-leads', name: 'Optimise website copy for more conversions' },
  'social-proof-compilation-agent': { cat: 'get-more-leads', name: 'Compile reviews and testimonials automatically' },
  'campaign-retro-analyser': { cat: 'get-more-leads', name: 'Analyse campaign results and learn what works' },
  'blog_draft_generator':   { cat: 'get-more-leads', name: 'Draft blog posts from a simple outline' },
  'seo_content_refresh_agent': { cat: 'get-more-leads', name: 'Refresh old content to rank higher' },
  'social_media_repurposing_agent': { cat: 'get-more-leads', name: 'Turn one piece of content into 10 social posts' },
  'lead_magnet_delivery_agent': { cat: 'get-more-leads', name: 'Auto-deliver lead magnets to new signups' },
  'salesforce_lead_intake_v1': { cat: 'get-more-leads', name: 'Qualify and enrich new leads automatically' },

  // ── Respond & Win Faster ──
  'template_5f984d8328fb': { cat: 'respond-win-faster', name: 'Qualify inbound leads instantly' },
  'template_e0f2da091f74': { cat: 'respond-win-faster', name: 'Answer sales calls and qualify on the spot' },
  'template_d9a3c6e5fe1d': { cat: 'respond-win-faster', name: 'Reply to customer questions 24/7' },
  'template_7785dd55ac86': { cat: 'respond-win-faster', name: 'Route support tickets to the right person' },
  'template_3a3aba846cf6': { cat: 'respond-win-faster', name: 'Handle support calls with natural voice' },
  'template_26bdbb26739d': { cat: 'respond-win-faster', name: 'Parse and route incoming emails instantly' },
  'customer_support_v1':   { cat: 'respond-win-faster', name: 'Handle inbound support calls automatically' },
  'voice_receptionist_v1': { cat: 'respond-win-faster', name: 'Answer every call like a professional receptionist' },
  'voice_booking_agent_v1': { cat: 'respond-win-faster', name: 'Take bookings over the phone automatically' },
  'inbound_lead_qualification_agent': { cat: 'respond-win-faster', name: 'Score and prioritise inbound leads' },
  'support_reply_draft_agent': { cat: 'respond-win-faster', name: 'Draft support replies in seconds' },
  'ticket_triage_agent':   { cat: 'respond-win-faster', name: 'Triage and classify tickets automatically' },
  'faq_retrieval_agent':   { cat: 'respond-win-faster', name: 'Answer FAQs from your knowledge base' },
  'customer_sentiment_escalation_agent': { cat: 'respond-win-faster', name: 'Detect unhappy customers and escalate fast' },
  'email_triage_agent':    { cat: 'respond-win-faster', name: 'Sort and prioritise incoming emails' },
  'ms365_email_triage_v1': { cat: 'respond-win-faster', name: 'Triage emails and suggest next actions' },
  'it-service-desk-triage': { cat: 'respond-win-faster', name: 'Triage IT support requests automatically' },
  'lead-qualification-scoring': { cat: 'respond-win-faster', name: 'Score leads so you talk to the best ones first' },
  'customer-voice-summariser': { cat: 'respond-win-faster', name: 'Summarise customer calls for quick review' },
  'support_conversation_summarizer': { cat: 'respond-win-faster', name: 'Summarise support threads in one click' },

  // ── Follow Up & Close More Deals ──
  'template_02855a3ad1fd': { cat: 'follow-up-close', name: 'Send personalised cold outreach emails' },
  'template_93dc0d035dd9': { cat: 'follow-up-close', name: 'Schedule meetings with prospects automatically' },
  'template_88d5f682da51': { cat: 'follow-up-close', name: 'Keep your CRM updated after every conversation' },
  'template_d7dcdbe785c8': { cat: 'follow-up-close', name: 'Chase overdue payments automatically' },
  'proposal_writer_v1':    { cat: 'follow-up-close', name: 'Draft proposals that close deals faster' },
  'crm_updater_v1':        { cat: 'follow-up-close', name: 'Clean and update CRM data automatically' },
  'sales-prospecting-assistant': { cat: 'follow-up-close', name: 'Find and research new prospects' },
  'cold-email-drafter':    { cat: 'follow-up-close', name: 'Draft cold emails that get replies' },
  'proposal-drafter':      { cat: 'follow-up-close', name: 'Create professional proposals from a brief' },
  'client-discovery-questionnaire': { cat: 'follow-up-close', name: 'Build discovery questionnaires for new clients' },
  'churn-risk-analyser':   { cat: 'follow-up-close', name: 'Spot customers at risk of leaving' },
  'lead_followup_email_generator': { cat: 'follow-up-close', name: 'Generate follow-up emails after first contact' },
  'sales_proposal_draft_agent': { cat: 'follow-up-close', name: 'Draft sales proposals from opportunity details' },
  'crm_conversation_summary_agent': { cat: 'follow-up-close', name: 'Summarise sales conversations for CRM' },
  'meeting_to_crm_update_agent': { cat: 'follow-up-close', name: 'Push meeting notes straight to CRM' },
  'salesforce_meeting_crm_v1': { cat: 'follow-up-close', name: 'Sync meeting outcomes to CRM records' },
  'voice-call-notes-and-tasks': { cat: 'follow-up-close', name: 'Turn call recordings into notes and tasks' },
  'customer_feedback_insight_agent': { cat: 'follow-up-close', name: 'Turn customer feedback into action items' },

  // ── Book & Manage Jobs ──
  'template_99642c101a77': { cat: 'book-manage-jobs', name: 'Onboard new customers step by step' },
  'template_510438ee5791': { cat: 'book-manage-jobs', name: 'Handle refunds and returns by policy' },
  'template_8c6971087144': { cat: 'book-manage-jobs', name: 'Process invoices from email and PDF' },
  'template_0239a36d012e': { cat: 'book-manage-jobs', name: 'Monitor inventory and trigger reorders' },
  'template_4cf224975b8c': { cat: 'book-manage-jobs', name: 'Take meeting notes and extract action items' },
  'template_fdbd16a313d7': { cat: 'book-manage-jobs', name: 'Classify and file incoming documents' },
  'voice_scheduler_v1':    { cat: 'book-manage-jobs', name: 'Let customers book appointments by phone' },
  'invoice_generator_v1':  { cat: 'book-manage-jobs', name: 'Generate invoices and quotes in seconds' },
  'meeting_minutes_v1':    { cat: 'book-manage-jobs', name: 'Turn meeting recordings into summaries' },
  'meeting-notes-assistant': { cat: 'book-manage-jobs', name: 'Capture meeting notes automatically' },
  'google_gmail_invoice_v1': { cat: 'book-manage-jobs', name: 'Pull invoices from Gmail automatically' },
  'google_drive_summarizer_v1': { cat: 'book-manage-jobs', name: 'Summarise documents from your drive' },
  'xero_invoice_processing_v1': { cat: 'book-manage-jobs', name: 'Process invoices end-to-end' },
  'xero_payment_followup_v1': { cat: 'book-manage-jobs', name: 'Follow up on overdue payments' },
  'meeting_notes_upload_v1': { cat: 'book-manage-jobs', name: 'Turn uploaded meeting notes into tasks' },
  'meeting_notes_connected_v1': { cat: 'book-manage-jobs', name: 'Join meetings and take notes live' },
  'invoice_data_extraction_agent': { cat: 'book-manage-jobs', name: 'Extract invoice data from uploaded docs' },
  'contract_review_assistant': { cat: 'book-manage-jobs', name: 'Review contracts and flag key clauses' },
  'maintenance-schedule-planner': { cat: 'book-manage-jobs', name: 'Plan maintenance schedules automatically' },
  'field-service-visit-summariser': { cat: 'book-manage-jobs', name: 'Summarise field service visits' },
  'supplier-onboarding-checklist': { cat: 'book-manage-jobs', name: 'Create supplier onboarding checklists' },
  'spreadsheet_data_classifier': { cat: 'book-manage-jobs', name: 'Classify spreadsheet data automatically' },
  'meeting_action_item_generator': { cat: 'book-manage-jobs', name: 'Extract action items from any meeting' },
  'weekly_operations_report_generator': { cat: 'book-manage-jobs', name: 'Generate weekly operations reports' },
  'project_status_summarizer': { cat: 'book-manage-jobs', name: 'Summarise project status in one click' },
  'internal_knowledge_assistant': { cat: 'book-manage-jobs', name: 'Answer team questions from your docs' },
  'calendar-capacity-analyser': { cat: 'book-manage-jobs', name: 'Analyse your calendar and find free time' },
  'email-digest-summariser': { cat: 'book-manage-jobs', name: 'Get a daily summary of your email threads' },
  'ms365_meeting_tasks_v1': { cat: 'book-manage-jobs', name: 'Turn meetings into tasks and follow-ups' },
  'pdf_summary_agent':      { cat: 'book-manage-jobs', name: 'Summarise PDFs and extract key points' },
  'policy_summarizer_agent': { cat: 'book-manage-jobs', name: 'Summarise policies into quick references' },
};

/* Fallback: map backend categories to outcome categories */
const CATEGORY_FALLBACK_MAP = {
  'sales': 'follow-up-close',
  'sales_marketing': 'get-more-leads',
  'Sales & Marketing': 'get-more-leads',
  'marketing': 'get-more-leads',
  'Marketing': 'get-more-leads',
  'Increase Sales': 'follow-up-close',
  'customer_service': 'respond-win-faster',
  'Customer Support': 'respond-win-faster',
  'customer_support': 'respond-win-faster',
  'Customer Service': 'respond-win-faster',
  'Customer Success': 'follow-up-close',
  'voice': 'respond-win-faster',
  'operations': 'book-manage-jobs',
  'Operations': 'book-manage-jobs',
  'finance': 'book-manage-jobs',
  'Finance & Admin': 'book-manage-jobs',
  'Finance & Accounting': 'book-manage-jobs',
  'productivity': 'book-manage-jobs',
  'Productivity': 'book-manage-jobs',
  'hr': 'book-manage-jobs',
  'People & HR': 'book-manage-jobs',
  'content': 'get-more-leads',
  'analytics': 'book-manage-jobs',
  'Data & Analytics': 'book-manage-jobs',
  'legal': 'book-manage-jobs',
  'Legal & Compliance': 'book-manage-jobs',
  'ai_ops': 'book-manage-jobs',
  'AI & Automation': 'book-manage-jobs',
  'IT & Support': 'respond-win-faster',
  'Product & Delivery': 'book-manage-jobs',
  'Strategy & Org Design': 'book-manage-jobs',
  'Health & Safety': 'book-manage-jobs',
  'Research & Analysis': 'book-manage-jobs',
  'Save Time': 'respond-win-faster',
};

function getOutcomeCategory(template) {
  if (TEMPLATE_OUTCOME_MAP[template.id]) return TEMPLATE_OUTCOME_MAP[template.id].cat;
  return CATEGORY_FALLBACK_MAP[template.category] || 'book-manage-jobs';
}

function getOutcomeName(template) {
  if (TEMPLATE_OUTCOME_MAP[template.id]) return TEMPLATE_OUTCOME_MAP[template.id].name;
  return template.name;
}

/* ═══════════════════════════════════════════════════════
   COMMAND BLOCK — "Not sure? Tell us your business"
   ═══════════════════════════════════════════════════════ */

const CommandBlock = ({ orgId }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!query.trim()) return;
    setCreating(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/command/create-worker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: query }),
        },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`"${data.worker?.name || 'AI Worker'}" created — ready for review`);
        navigate('/automations');
      } else {
        toast.error('Could not create worker. Try again.');
      }
    } catch {
      toast.error('Something went wrong.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid="marketplace-command-block"
      className="relative overflow-hidden rounded-2xl border border-[rgba(0,229,192,0.15)] bg-gradient-to-br from-[rgba(0,229,192,0.04)] to-[rgba(0,229,192,0.01)] p-6 mb-10"
    >
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={18} weight="fill" className="text-[#00E5C0]" />
          <span className="text-xs font-semibold text-[#00E5C0] uppercase tracking-[0.08em]">Not sure what to set up?</span>
        </div>
        <p className="text-[#F0F4F8] text-lg font-semibold mb-1">Tell us about your business</p>
        <p className="text-[#B0B8C4] text-sm mb-4">Describe what you do and we'll build the right AI workers for you.</p>
        <div className="flex gap-3">
          <input
            data-testid="marketplace-command-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="e.g. I run a plumbing company and want to catch more leads..."
            className="flex-1 px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]/40 transition-colors"
          />
          <button
            data-testid="marketplace-command-submit"
            onClick={handleSubmit}
            disabled={creating || !query.trim()}
            className="px-5 py-3 bg-[#00E5C0] text-[#12161C] rounded-xl text-sm font-bold hover:bg-[#00D4B0] disabled:opacity-40 transition-all flex items-center gap-2 whitespace-nowrap"
          >
            {creating ? <CircleNotch size={16} className="animate-spin" /> : <Rocket size={16} weight="bold" />}
            Build this for me
          </button>
        </div>
      </div>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#00E5C0]/[0.02] rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   TEMPLATE CARD — Outcome-first design
   ═══════════════════════════════════════════════════════ */

const TemplateCard = ({ template, outcomeName, category, onSetup }) => {
  const catDef = OUTCOME_CATEGORIES.find(c => c.id === category);

  return (
    <div
      data-testid={`template-card-${template.id}`}
      className="group bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 hover:border-[rgba(255,255,255,0.1)] hover:-translate-y-[3px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.45)] shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)] transition-all duration-200 flex flex-col"
    >
      <div className="flex-1">
        <p className="text-[#F0F4F8] text-sm font-semibold leading-snug mb-2 group-hover:text-[#00E5C0] transition-colors">
          {outcomeName}
        </p>
        {catDef && (
          <span
            data-testid={`template-tag-${template.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ background: catDef.bgColor, color: catDef.color, border: `1px solid ${catDef.borderColor}` }}
          >
            <catDef.icon size={10} weight="bold" />
            {catDef.label}
          </span>
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-[rgba(255,255,255,0.04)]">
        <button
          data-testid={`setup-btn-${template.id}`}
          onClick={() => onSetup(template.id, template.name)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] hover:border-[rgba(0,229,192,0.3)] transition-all"
        >
          <Play size={14} weight="fill" />
          Set This Up
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CATEGORY SECTION
   ═══════════════════════════════════════════════════════ */

const CategorySection = ({ category, templates, onSetup, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const catDef = OUTCOME_CATEGORIES.find(c => c.id === category);
  if (!catDef || templates.length === 0) return null;

  const Icon = catDef.icon;
  const shown = expanded ? templates : templates.slice(0, 4);

  return (
    <section data-testid={`category-section-${category}`} className="mb-12">
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-[rgba(255,255,255,0.08)]">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: catDef.bgColor, border: `1px solid ${catDef.borderColor}` }}
        >
          <Icon size={18} weight="duotone" style={{ color: catDef.color }} />
        </div>
        <div>
          <h2 className="text-base font-bold text-[#F0F4F8]">{catDef.label}</h2>
          <p className="text-xs text-[#7D8896]">{catDef.description}</p>
        </div>
        <span className="ml-auto px-2 py-0.5 bg-[rgba(255,255,255,0.04)] rounded text-[10px] text-[#7D8896] font-medium">
          {templates.length}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {shown.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            outcomeName={getOutcomeName(t)}
            category={category}
            onSetup={onSetup}
          />
        ))}
      </div>
      {templates.length > 4 && (
        <button
          data-testid={`toggle-${category}`}
          onClick={() => setExpanded(!expanded)}
          className="mt-4 flex items-center gap-1.5 text-xs text-[#B0B8C4] hover:text-[#00E5C0] transition-colors mx-auto"
        >
          {expanded ? 'Show less' : `Show all ${templates.length}`}
          <CaretRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      )}
    </section>
  );
};

/* ═══════════════════════════════════════════════════════
   SETUP FLOW MODAL — Click → Progress → Auto-Test → Result
   Zero questions. Instant outcome. Proof of value.
   ═══════════════════════════════════════════════════════ */

const PROGRESS_MESSAGES = [
  'Configuring your system...',
  'Connecting the pieces...',
  'Almost there...',
];

const TEST_MESSAGES = [
  'Running a test with a sample enquiry...',
  'Generating response...',
  'Preparing results...',
];

const SetupFlowModal = ({ template, outcomeName, orgId, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { mode } = useMode();
  const [phase, setPhase] = useState('progress'); // progress | testing | result | error
  const [progressMsg, setProgressMsg] = useState(PROGRESS_MESSAGES[0]);
  const [agent, setAgent] = useState(null);
  const [testResult, setTestResult] = useState(null); // { sample_input, output }
  const [error, setError] = useState('');
  const [retesting, setRetesting] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [toolStatus, setToolStatus] = useState(null); // { missing_tools, all_connected, tool_status }

  // Fetch tool status after agent is created
  const fetchToolStatus = useCallback(async (agentId) => {
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/worker/${agentId}/tool-status`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setToolStatus(data);
      }
    } catch { /* silent */ }
  }, [orgId]);

  // Auto-build + auto-test on mount — zero questions, zero clicks
  useEffect(() => {
    if (!template) return;
    let cancelled = false;

    const doSetupAndTest = async () => {
      // Phase 1: Install
      const progressInterval = setInterval(() => {
        setProgressMsg((prev) => {
          const idx = PROGRESS_MESSAGES.indexOf(prev);
          return PROGRESS_MESSAGES[Math.min(idx + 1, PROGRESS_MESSAGES.length - 1)];
        });
      }, 800);

      let agentId, agentName;
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/agent-templates/${template.id}/instantiate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          },
          orgId
        );

        clearInterval(progressInterval);
        if (cancelled) return;

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || 'Setup failed. Try again.');
          setPhase('error');
          return;
        }

        const data = await res.json();
        if (data.requires_wizard) {
          navigate(`/partner-pro/ai-workforce/agents/${data.agent_id}/wizard`);
          onClose();
          return;
        }

        agentId = data.agent_id;
        agentName = outcomeName || template.name || 'AI Worker';
        setAgent({ id: agentId, name: agentName, capabilityProfile: data.capability_profile || 'text_agent' });

        // Fetch tool status in parallel with auto-test
        if (!cancelled) fetchToolStatus(agentId);
      } catch {
        clearInterval(progressInterval);
        if (!cancelled) {
          setError('Something went wrong. Try again.');
          setPhase('error');
        }
        return;
      }

      // Phase 2: Auto-test
      if (cancelled) return;
      setPhase('testing');
      setProgressMsg(TEST_MESSAGES[0]);

      const testInterval = setInterval(() => {
        setProgressMsg((prev) => {
          const idx = TEST_MESSAGES.indexOf(prev);
          return TEST_MESSAGES[Math.min(idx + 1, TEST_MESSAGES.length - 1)];
        });
      }, 900);

      try {
        const testRes = await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/command/auto-test/${agentId}`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' } },
          orgId
        );

        clearInterval(testInterval);
        if (cancelled) return;

        if (testRes.ok) {
          const testData = await testRes.json();
          setTestResult({
            sampleInput: testData.sample_input,
            output: testData.output,
            workItemId: testData.work_item_id,
          });
          setPhase('result');
          onComplete?.();
        } else {
          // Test failed but setup succeeded — show result without test
          setTestResult(null);
          setPhase('result');
          onComplete?.();
        }
      } catch {
        clearInterval(testInterval);
        if (!cancelled) {
          // Test failed but setup succeeded — show result without test
          setTestResult(null);
          setPhase('result');
          onComplete?.();
        }
      }
    };

    doSetupAndTest();
    return () => { cancelled = true; };
  }, [template, orgId, outcomeName, navigate, onClose, onComplete, fetchToolStatus]);

  // Re-test handler
  const handleRetest = async () => {
    if (!agent) return;
    setRetesting(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/command/auto-test/${agent.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setTestResult({
          sampleInput: data.sample_input,
          output: data.output,
          workItemId: data.work_item_id,
        });
      }
    } catch { /* silent */ }
    setRetesting(false);
  };

  if (!template) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="setup-flow-modal">
      <div
        className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── PROGRESS PHASE ── */}
        {phase === 'progress' && (
          <div className="p-8 text-center" data-testid="setup-phase-progress">
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-2 border-[rgba(0,229,192,0.15)]" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#00E5C0] animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Rocket size={24} weight="duotone" className="text-[#00E5C0]" />
              </div>
            </div>
            <p className="text-lg font-bold text-[#F0F4F8] mb-1">Setting this up for you...</p>
            <p className="text-sm text-[#7D8896] animate-pulse">{progressMsg}</p>
          </div>
        )}

        {/* ── TESTING PHASE ── */}
        {phase === 'testing' && (
          <div className="p-8 text-center" data-testid="setup-phase-testing">
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-2 border-[rgba(0,229,192,0.15)]" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#00E5C0] animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <TestTube size={24} weight="duotone" className="text-[#00E5C0]" />
              </div>
            </div>
            <p className="text-lg font-bold text-[#F0F4F8] mb-1">Testing with a real scenario...</p>
            <p className="text-sm text-[#7D8896] animate-pulse">{progressMsg}</p>
          </div>
        )}

        {/* ── RESULT PHASE — Proof of value ── */}
        {phase === 'result' && agent && (
          <div className="p-6" data-testid="setup-phase-result">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center">
                <CheckCircle size={22} weight="fill" className="text-[#00E5C0]" />
              </div>
              <button onClick={onClose} className="text-[#7D8896] hover:text-[#F0F4F8] transition-colors" data-testid="close-setup-modal">
                <X size={18} />
              </button>
            </div>

            <h3 className="text-lg font-bold text-[#F0F4F8] mb-1" data-testid="setup-ready-heading">
              Your {agent.name} is ready
            </h3>

            {/* Integration CTAs — shown when tools are missing */}
            {toolStatus && toolStatus.missing_count > 0 && (
              <div className="mt-3 mb-4" data-testid="integration-cta-section">
                <div className="bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.15)] rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1.5">
                    <Plugs size={14} weight="bold" />
                    Connect to go live
                  </p>
                  <div className="space-y-2">
                    {toolStatus.missing_tools.map((tool) => (
                      <button
                        key={tool.tool}
                        data-testid={`connect-cta-${tool.tool}`}
                        onClick={() => {
                          navigate(`/apps?highlight=${tool.primary_app}`);
                          onClose();
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 bg-[#1F2937] rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-[rgba(245,158,11,0.3)] transition-all group"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-[rgba(245,158,11,0.1)] flex items-center justify-center">
                            {tool.tool === 'email' && <EnvelopeSimple size={13} weight="bold" className="text-amber-400" />}
                            {tool.tool === 'calendar' && <CalendarCheck size={13} weight="bold" className="text-amber-400" />}
                            {tool.tool === 'sms' && <ChatText size={13} weight="bold" className="text-amber-400" />}
                            {tool.tool === 'crm' && <Users size={13} weight="bold" className="text-amber-400" />}
                            {tool.tool === 'phone' && <Phone size={13} weight="bold" className="text-amber-400" />}
                          </div>
                          <div className="text-left">
                            <p className="text-xs text-[#F0F4F8] font-medium">Connect {tool.label}</p>
                            <p className="text-[10px] text-[#7D8896]">{tool.description}</p>
                          </div>
                        </div>
                        <ArrowRight size={14} className="text-[#7D8896] group-hover:text-amber-400 transition-colors" />
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#7D8896] mt-2">
                    Your worker is active but will need these connections to handle real messages.
                  </p>
                </div>
              </div>
            )}

            {/* All tools connected badge */}
            {toolStatus && toolStatus.all_connected && toolStatus.tool_status?.length > 0 && (
              <div className="mt-3 mb-4 flex items-center gap-2 px-3 py-2 bg-[rgba(0,229,192,0.04)] rounded-lg border border-[rgba(0,229,192,0.1)]" data-testid="tools-connected-badge">
                <CheckCircle size={14} weight="fill" className="text-[#00E5C0]" />
                <p className="text-xs text-[#00E5C0] font-medium">
                  All tools connected — this worker is ready to go live
                </p>
              </div>
            )}

            {/* Test result display */}
            {testResult ? (
              <div className="mt-4 mb-5" data-testid="auto-test-result">
                <p className="text-xs font-semibold text-[#00E5C0] uppercase tracking-[0.08em] mb-3">
                  Here's what this will do for you
                </p>

                {/* Sample input */}
                <div className="bg-[#1F2937] rounded-lg p-3 mb-2 border border-[rgba(255,255,255,0.04)]">
                  <p className="text-[10px] text-[#7D8896] uppercase tracking-[0.08em] mb-1">Sample enquiry</p>
                  <p className="text-xs text-[#B0B8C4]" data-testid="test-input-name">
                    <span className="text-[#F0F4F8] font-medium">{testResult.sampleInput?.customer_name}</span>
                  </p>
                  <p className="text-xs text-[#B0B8C4] mt-0.5 leading-relaxed" data-testid="test-input-enquiry">
                    "{testResult.sampleInput?.enquiry}"
                  </p>
                </div>

                {/* AI output */}
                <div className="bg-[rgba(0,229,192,0.04)] rounded-lg p-3 border border-[rgba(0,229,192,0.1)]">
                  <p className="text-[10px] text-[#00E5C0] uppercase tracking-[0.08em] mb-1">AI response</p>
                  <p className="text-xs text-[#F0F4F8] leading-relaxed whitespace-pre-wrap" data-testid="test-output">
                    {testResult.output}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#B0B8C4] mb-5">
                Added to your AI team in <span className="text-amber-400 font-medium">review mode</span> — nothing goes out without your approval.
              </p>
            )}

            {/* Primary actions */}
            <div className="flex gap-3 mb-3">
              <button
                data-testid="setup-activate"
                onClick={() => {
                  navigate('/automations');
                  onClose();
                }}
                className="flex-1 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] rounded-lg text-sm font-bold hover:bg-[#00D4B0] transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} weight="bold" />
                Looks good — activate
              </button>
              <button
                data-testid="setup-retest"
                onClick={handleRetest}
                disabled={retesting}
                className="px-4 py-2.5 border border-[rgba(255,255,255,0.1)] text-[#F0F4F8] rounded-lg text-sm font-medium hover:bg-[rgba(255,255,255,0.04)] transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {retesting ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} className="rotate-180" />}
                Test again
              </button>
            </div>

            {/* Secondary actions */}
            <div className="flex items-center justify-between">
              <button
                data-testid="setup-view-team"
                onClick={() => {
                  navigate('/automations');
                  onClose();
                }}
                className="text-xs text-[#B0B8C4] hover:text-[#00E5C0] transition-colors flex items-center gap-1.5"
              >
                <Users size={12} />
                View in AI Team
              </button>
              <button
                data-testid="setup-done"
                onClick={onClose}
                className="text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
              >
                Done
              </button>
            </div>

            {/* Personalisation + Design mode */}
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.04)]">
              {!showRefine ? (
                <button
                  data-testid="setup-personalise-toggle"
                  onClick={() => setShowRefine(true)}
                  className="text-xs text-[#B0B8C4] hover:text-[#00E5C0] transition-colors flex items-center gap-1.5"
                >
                  <PencilSimple size={12} />
                  Adjust tone or personalise
                </button>
              ) : (
                <PersonalisePanel agent={agent} orgId={orgId} onClose={onClose} />
              )}

              {mode === 'design' && (
                <button
                  data-testid="setup-edit-system"
                  onClick={() => {
                    navigate(`/unified-agent-builder/${agent.id}`);
                    onClose();
                  }}
                  className="mt-2 text-xs text-[#7D8896] hover:text-amber-400 transition-colors flex items-center gap-1.5"
                >
                  <Plugs size={12} />
                  Edit this system (Design Mode)
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── ERROR PHASE ── */}
        {phase === 'error' && (
          <div className="p-6 text-center" data-testid="setup-phase-error">
            <div className="w-11 h-11 mx-auto mb-4 rounded-full bg-[rgba(255,107,107,0.1)] flex items-center justify-center">
              <Warning size={22} weight="fill" className="text-[#FF6B6B]" />
            </div>
            <p className="text-[#F0F4F8] font-semibold mb-1">Setup didn't complete</p>
            <p className="text-sm text-[#7D8896] mb-5">{error}</p>
            <div className="flex gap-3">
              <button
                data-testid="setup-retry"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] rounded-lg text-sm font-bold hover:bg-[#00D4B0] transition-colors"
              >
                Try again
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 border border-[rgba(255,255,255,0.1)] text-[#B0B8C4] rounded-lg text-sm hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PERSONALISE PANEL — Optional post-setup refinement
   ═══════════════════════════════════════════════════════ */

const PersonalisePanel = ({ agent, orgId, onClose }) => {
  const navigate = useNavigate();
  const [tone, setTone] = useState('');
  const [context, setContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!tone && !context) return;
    setSaving(true);
    try {
      // Use command to refine the worker with additional context
      const refinement = [];
      if (tone) refinement.push(`Tone: ${tone}`);
      if (context) refinement.push(`Business context: ${context}`);

      await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/command/create-worker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            intent: `Refine worker "${agent.name}": ${refinement.join('. ')}`,
          }),
        },
        orgId
      );
      setSaved(true);
      setTimeout(() => {
        navigate('/automations');
        onClose();
      }, 1200);
    } catch {
      // Silent — refinement is optional
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="flex items-center gap-2 py-2" data-testid="personalise-saved">
        <CheckCircle size={14} weight="fill" className="text-[#00E5C0]" />
        <span className="text-xs text-[#00E5C0]">Saved</span>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-2" data-testid="personalise-panel">
      <div>
        <label className="text-[10px] text-[#7D8896] uppercase tracking-[0.08em] mb-1 block">Reply tone (optional)</label>
        <div className="flex gap-2">
          {['Professional', 'Friendly', 'Direct'].map((t) => (
            <button
              key={t}
              data-testid={`tone-${t.toLowerCase()}`}
              onClick={() => setTone(tone === t ? '' : t)}
              className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                tone === t
                  ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]'
                  : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-[#7D8896] uppercase tracking-[0.08em] mb-1 block">Business context (optional)</label>
        <input
          data-testid="personalise-context"
          type="text"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="e.g. We're a plumbing company in Sydney"
          className="w-full px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]/40"
        />
      </div>
      <button
        data-testid="personalise-save"
        onClick={handleSave}
        disabled={saving || (!tone && !context)}
        className="px-4 py-2 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] rounded-lg text-xs font-semibold hover:bg-[rgba(0,229,192,0.15)] disabled:opacity-40 transition-all flex items-center gap-1.5"
      >
        {saving ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} />}
        Save
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */

const ServiceMarketplace = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { planLimits } = usePlan();
  const orgId = activeOrganization?.organization_id;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [setupTarget, setSetupTarget] = useState(null); // { template, outcomeName }

  const fetchTemplates = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      let allTemplates = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({ limit: '100', sort: 'recommended', page: String(page) });
        if (search) params.set('q', search);
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/agent-templates/marketplace?${params}`,
          {},
          orgId
        );
        if (res.ok) {
          const data = await res.json();
          const batch = data.templates || [];
          allTemplates = [...allTemplates, ...batch];
          hasMore = batch.length === 100 && page < (data.total_pages || 1);
          page++;
        } else {
          hasMore = false;
        }
      }
      setTemplates(allTemplates);
    } catch (err) {
      console.error('Error fetching marketplace templates:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, search]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSetup = (templateId, templateName) => {
    if (planLimits?.max_agents === 0) {
      toast.error('Your plan does not allow creating agents. Please upgrade.');
      return;
    }
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    setSetupTarget({ template: tmpl, outcomeName: getOutcomeName(tmpl) });
  };

  /* Group templates into outcome categories */
  const grouped = {};
  OUTCOME_CATEGORIES.forEach(c => { grouped[c.id] = []; });
  templates.forEach(t => {
    const cat = getOutcomeCategory(t);
    if (grouped[cat]) grouped[cat].push(t);
  });

  /* Filter by selected category */
  const categoriesToShow = activeCategory
    ? OUTCOME_CATEGORIES.filter(c => c.id === activeCategory)
    : OUTCOME_CATEGORIES;

  /* Search filter — match against outcome name */
  const filterBySearch = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(t => {
      const name = getOutcomeName(t).toLowerCase();
      return name.includes(q) || (t.name || '').toLowerCase().includes(q);
    });
  };

  return (
    <div className="min-h-screen bg-[#1F2937] p-6 lg:p-8" data-testid="service-marketplace-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#F0F4F8] mb-1" data-testid="marketplace-heading">
            Grow Your Business on Autopilot
          </h1>
          <p className="text-sm text-[#7D8896]">
            Pick a result you want. We'll set up an AI worker to make it happen.
          </p>
        </div>

        {/* Command Block */}
        <CommandBlock orgId={orgId} />

        {/* Category Pills + Search */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            data-testid="filter-all"
            onClick={() => setActiveCategory(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              !activeCategory
                ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]'
                : 'bg-transparent text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
            }`}
          >
            All
          </button>
          {OUTCOME_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setActiveCategory(isActive ? null : cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  isActive
                    ? 'text-white'
                    : 'bg-transparent text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
                }`}
                style={isActive ? { background: cat.bgColor, borderColor: cat.borderColor, color: cat.color } : {}}
              >
                <Icon size={12} weight="bold" />
                {cat.label}
              </button>
            );
          })}
          <div className="ml-auto relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
            <input
              data-testid="marketplace-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 pr-3 py-1.5 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-full text-xs text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]/40 w-48 transition-colors"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20" data-testid="marketplace-loading">
            <CircleNotch size={28} className="animate-spin text-[#00E5C0]" />
          </div>
        )}

        {/* Category Sections */}
        {!loading && categoriesToShow.map((cat) => {
          const filtered = filterBySearch(grouped[cat.id] || []);
          return (
            <CategorySection
              key={cat.id}
              category={cat.id}
              templates={filtered}
              onSetup={handleSetup}
              defaultExpanded={!!activeCategory}
            />
          );
        })}

        {/* Empty state */}
        {!loading && templates.length === 0 && (
          <div className="text-center py-20" data-testid="marketplace-empty">
            <Robot size={40} className="mx-auto text-[#7D8896] mb-3" />
            <p className="text-[#B0B8C4] text-sm">No templates found{search ? ` for "${search}"` : ''}.</p>
          </div>
        )}

        {/* Setup Flow Modal */}
        {setupTarget && (
          <SetupFlowModal
            template={setupTarget.template}
            outcomeName={setupTarget.outcomeName}
            orgId={orgId}
            onClose={() => setSetupTarget(null)}
            onComplete={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default ServiceMarketplace;
